import React, { useEffect, useState } from 'react';
import { Box, IconButton, Tab, Tabs } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import clsx from 'clsx';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { cloneDeep } from 'lodash';

import SelectFormField from '@components/form-fields/SelectFormField';
import DatePickerFormField from '@components/form-fields/DatePickerFormField';
import CurrencyFormField from '@components/form-fields/CurrencyFormField';
import PercentageFormField from '@components/form-fields/PercentageFormField';
import CategoryRepeater from './CategoryRepeater';

import {
  ITreatyLovs,
  ITreatyDetails,
  ITreatyErrors,
  ITreatyLayer,
} from '../..';
import { MAIN_THEME } from '@styles';

export interface ITabsRepeater {
  values: ITreatyDetails;
  onChange?: (allValues: ITreatyDetails) => void;
  errors: ITreatyErrors;
  updateErrors: (errors: ITreatyErrors) => void;
  lovs: ITreatyLovs;
  currencySymbols: Record<string, string>;
  tabsTitle: string;
  maxTabs?: number;
  minTabs?: number;
  showTabs?: boolean;
  isDisabled?: boolean;
}

const useStyles = makeStyles()(() => ({
  container: {
    width: '100%',
    boxSizing: 'border-box',
  },
  tabLabel: {
    fontSize: MAIN_THEME.fontSize.sm,
    fontFamily: MAIN_THEME.font.regular,
    textTransform: 'none',
    lineHeight: '18px',
    color: MAIN_THEME.color.black,
    display: 'flex',
    alignItems: 'center',
  },
  tabActiveTitle: {
    fontFamily: `${MAIN_THEME.font.medium} !important`,
    fontWeight: 600,
  },
  tabIconButton: {
    marginLeft: '8px',
    padding: 0,
  },
  tabsContainer: {
    display: 'flex',
    alignItems: 'center',
    borderBottom: `1px solid ${MAIN_THEME.color.white86}`,
  },
  tabsRoot: {
    minHeight: 'auto',
    overflowX: 'auto',
    '& .MuiTabs-flexContainer': {
      borderBottom: 'none',
    },
  },
  indicator: {
    display: 'none',
  },
  tabRoot: {
    textTransform: 'none',
    minWidth: 72,
    minHeight: 'auto',
    padding: '6px 12px',
    border: `1px solid ${MAIN_THEME.color.white92}`,
    borderBottom: 'none',
    borderRadius: '4px 4px 0 0',
    marginRight: '2px',
    '&.Mui-selected': {
      fontWeight: 600,
    },
  },
  addBtn: {
    marginLeft: '2px',
    minWidth: 'auto',
    minHeight: 'auto',
    backgroundColor: 'transparent',
    padding: 0,
    margin: '0 0 0 10px',
    alignSelf: 'center',
    border: 'none',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  tabPanel: {
    boxSizing: 'border-box',
    border: `1px solid ${MAIN_THEME.color.white92}`,
    borderTop: 0,
    position: 'relative',
    padding: '16px',
  },
  tabPanelContent: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 32%)',
    gap: '2%',
  },
}));

const TabsRepeater: React.FC<ITabsRepeater> = ({
  values,
  onChange = () => {},
  errors,
  updateErrors,
  lovs,
  currencySymbols,
  tabsTitle,
  showTabs = true,
  isDisabled = false,
}) => {
  const { classes } = useStyles();
  const tabs = Object.keys(values.layers);
  const [activeTab, setActiveTab] = useState<string>(tabs[0] || '');

  useEffect(() => {
    // If the treatyType changes to "non_proportional", force activeTab to "tab_0"
    if (values?.treatyType?.toLowerCase() === 'non_proportional') {
      setActiveTab('tab_0');
    }
  }, [values?.treatyType]);

  const handleChangeTab = (event: React.SyntheticEvent, newTab: string) => {
    setActiveTab(newTab);
  };

  const handleAddTab = () => {
    const newTabKey = `tab_${tabs.length}`;

    const newValues = cloneDeep(values) as ITreatyDetails;
    newValues.layers[newTabKey] = {
      treatySubType: 'QuotaShare',
      retentionShare: '',
      paymentDate: '',
      paymentAmount: '',
      totalTreatyPremium: '',
      totalExpectedPremium: '',
      premiumRate: '',
      categories: [
        {
          category: '',
          minimumLayerLimit: '',
          maximumLayerLimit: '',
          retentionLimit: '',
          priorityLimit: '',
          liabilityLimit: '',
          aggregateLimit: '',
          commissionRate: '',
        },
      ],
    };

    const newErrors = cloneDeep(errors) as ITreatyErrors;
    newErrors.layers[newTabKey] = {
      treatySubType: '',
      retentionShare: '',
      paymentDate: '',
      paymentAmount: '',
      totalTreatyPremium: '',
      totalExpectedPremium: '',
      premiumRate: '',
      categories: [
        {
          category: '',
          minimumLayerLimit: '',
          maximumLayerLimit: '',
          retentionLimit: '',
          priorityLimit: '',
          liabilityLimit: '',
          aggregateLimit: '',
          commissionRate: '',
        },
      ],
    };

    setActiveTab(newTabKey);
    onChange(newValues);
    updateErrors(newErrors);
  };

  const handleDeleteTab = (layerKey: string) => {
    const newValues = cloneDeep(values) as ITreatyDetails;
    const newErrors = cloneDeep(errors) as ITreatyErrors;

    // Remove the layer from both structures
    delete newValues.layers[layerKey];
    delete newErrors.layers[layerKey];

    // Re-index the layers so they remain `tab_0, tab_1, ...`
    const layerKeys = Object.keys(newValues.layers);

    newValues.layers = layerKeys.reduce(
      (acc, key, index) => {
        acc[`tab_${index}`] = newValues.layers[key];
        return acc;
      },
      {} as ITreatyDetails['layers']
    );

    newErrors.layers = layerKeys.reduce(
      (acc, key, index) => {
        acc[`tab_${index}`] = newErrors.layers[key];
        return acc;
      },
      {} as ITreatyErrors['layers']
    );

    const newTabs = Object.keys(newValues.layers);
    // Set activeTab to the first tab if it exists
    setActiveTab(newTabs.length > 0 ? newTabs[0] : '');

    onChange(newValues);
    updateErrors(newErrors);
  };

  const handleFieldChange = (
    value: any,
    fieldName: keyof ITreatyLayer,
    tabKey: string
  ) => {
    const newValues = cloneDeep(values) as ITreatyDetails;
    newValues.layers[tabKey][fieldName] = value;
    onChange(newValues);
  };

  return (
    <section className={classes.container}>
      {/* TABS HEADER */}
      <Box className={classes.tabsContainer}>
        {showTabs && (
          <Tabs
            value={activeTab}
            onChange={handleChangeTab}
            classes={{
              root: classes.tabsRoot,
              indicator: classes.indicator,
            }}
            variant="scrollable"
            scrollButtons="auto"
          >
            {tabs.map((tabKey, index) => (
              <Tab
                key={tabKey}
                value={tabKey}
                className={classes.tabRoot}
                label={
                  <div
                    className={clsx(classes.tabLabel, {
                      [classes.tabActiveTitle]: activeTab === tabKey,
                    })}
                  >
                    {tabsTitle} {index + 1}
                    {!isDisabled && tabs.length > 1 && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteTab(tabKey);
                        }}
                        className={classes.tabIconButton}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )}
                  </div>
                }
              />
            ))}
          </Tabs>
        )}
        {/* ADD TAB BUTTON */}
        {showTabs && (
          <IconButton
            className={classes.addBtn}
            onClick={handleAddTab}
            disabled={isDisabled}
            aria-label="Add"
          >
            <AddIcon color="primary" />
          </IconButton>
        )}
      </Box>

      {/* TAB PANELS */}
      {Object.keys(values.layers).map((layerKey, index) => {
        // Only render if this is the active tab
        if (layerKey !== activeTab) return null;

        const tabValues = values.layers[layerKey];
        const tabErrors = errors.layers[layerKey];
        const currencySymbol = currencySymbols?.[values.currency];

        return (
          <Box key={layerKey} className={classes.tabPanel}>
            <div className={classes.tabPanelContent}>
              <SelectFormField
                name="treatySubType"
                title="Type*"
                value={tabValues?.treatySubType}
                placeholder="Select Type"
                selectOptions={
                  lovs.treatySubType[
                    values?.treatyType?.toLowerCase() as keyof ITreatyLovs['treatySubType']
                  ]
                }
                onChange={(v) =>
                  handleFieldChange(v, 'treatySubType', layerKey)
                }
                error={tabErrors?.treatySubType}
                disabled={isDisabled}
                material
              />

              {tabValues?.treatySubType?.toLowerCase() === 'quotashare' && (
                <PercentageFormField
                  name="retentionShare"
                  title="Retention Share %*"
                  placeholder="Enter Quota Share"
                  value={tabValues?.retentionShare}
                  onChange={(e) =>
                    handleFieldChange(
                      e.target.value,
                      'retentionShare',
                      layerKey
                    )
                  }
                  onBlur={() => {}}
                  error={tabErrors?.retentionShare}
                  disabled={isDisabled}
                  material
                />
              )}

              {values?.treatyType?.toLowerCase() === 'non_proportional' && (
                <>
                  <DatePickerFormField
                    name="paymentDate"
                    title="Payment Date*"
                    value={tabValues?.paymentDate}
                    onDateChange={(v) =>
                      handleFieldChange(v, 'paymentDate', layerKey)
                    }
                    error={tabErrors?.paymentDate}
                    disabled={isDisabled}
                    material
                  />
                  <CurrencyFormField
                    name="paymentAmount"
                    title="Payment Amount*"
                    placeholder="Enter Payment Amount"
                    value={tabValues?.paymentAmount}
                    onChange={(e) =>
                      handleFieldChange(
                        e.target.value,
                        'paymentAmount',
                        layerKey
                      )
                    }
                    currencySymbol={currencySymbol}
                    error={tabErrors?.paymentAmount}
                    disabled={isDisabled}
                    material
                  />
                  <CurrencyFormField
                    name="totalTreatyPremium"
                    title="Total Treaty Premium*"
                    placeholder="Enter Total Treaty Premium"
                    value={tabValues?.totalTreatyPremium}
                    onChange={(e) =>
                      handleFieldChange(
                        e.target.value,
                        'totalTreatyPremium',
                        layerKey
                      )
                    }
                    currencySymbol={currencySymbol}
                    error={tabErrors?.totalTreatyPremium}
                    disabled={isDisabled}
                    material
                  />
                  <CurrencyFormField
                    name="totalExpectedPremium"
                    title="Total Expected Premium*"
                    placeholder="Enter Total Expected Premium"
                    value={tabValues?.totalExpectedPremium}
                    onChange={(e) =>
                      handleFieldChange(
                        e.target.value,
                        'totalExpectedPremium',
                        layerKey
                      )
                    }
                    currencySymbol={currencySymbol}
                    error={tabErrors?.totalExpectedPremium}
                    disabled={isDisabled}
                    material
                  />
                  <CurrencyFormField
                    name="premiumRate"
                    title="Premium Rate*"
                    placeholder="Enter Premium Rate"
                    value={tabValues?.premiumRate}
                    onChange={(e) =>
                      handleFieldChange(e.target.value, 'premiumRate', layerKey)
                    }
                    currencySymbol={currencySymbol}
                    error={tabErrors?.premiumRate}
                    disabled={isDisabled}
                    material
                  />
                </>
              )}
            </div>

            <CategoryRepeater
              treatyValues={values}
              currentLayerValues={tabValues}
              currentLayerErrors={tabErrors}
              isDisabled={isDisabled}
              lovs={lovs}
              currencySymbols={currencySymbols}
              onChange={(layerValues) =>
                handleFieldChange(layerValues, 'categories', layerKey)
              }
              updateErrors={(layerErrors) => {
                const newErrors = cloneDeep(errors);
                newErrors.layers[layerKey] = layerErrors;
                updateErrors(newErrors);
              }}
            />
          </Box>
        );
      })}
    </section>
  );
};

export default TabsRepeater;
